import { useState, useEffect } from "react";
import { User } from "@/features/user";
import { isStagingOrDev } from "@/app-config";

// TOS was updated on June 27, 2023 at 16:32 Sydney time (Unix timestamp)
const TOS_UPDATED_TIMESTAMP = 1_687_847_488;

// Contact details confirmation interval in days
const CONTACT_DETAILS_CONFIRMATION_DAYS = 90;

interface TosCheckResult {
  showTosModal: boolean;
  showContactDetailsModal: boolean;
  onTosAccepted: () => void;
  onContactDetailsConfirmed: () => void;
}

/**
 * Hook to check if user needs to accept updated TOS or confirm contact details.
 * This replaces the old Angular $rootScope.checkTosStatus() function.
 *
 * The TOS check is skipped on staging/dev environments.
 */
export function useTosStatusCheck(user: User | undefined): TosCheckResult {
  const [showTosModal, setShowTosModal] = useState(false);
  const [showContactDetailsModal, setShowContactDetailsModal] = useState(false);

  useEffect(() => {
    if (!user?.preferences) {
      return;
    }

    // Skip TOS check on staging/dev
    if (isStagingOrDev()) {
      return;
    }

    const { readTosAt, confirmContactDetails } = user.preferences;

    // Check if user needs to accept updated TOS
    const needsToAcceptTos =
      !readTosAt ||
      new Date(readTosAt).getTime() < TOS_UPDATED_TIMESTAMP * 1000;

    if (needsToAcceptTos) {
      setShowTosModal(true);
      return; // Don't show contact details modal if TOS modal is shown
    }

    // Check if user needs to confirm contact details (every 90 days)
    if (confirmContactDetails) {
      const daysSinceConfirmation = Math.floor(
        (Date.now() - new Date(confirmContactDetails).getTime()) /
          (1000 * 60 * 60 * 24)
      );

      if (daysSinceConfirmation > CONTACT_DETAILS_CONFIRMATION_DAYS) {
        setShowContactDetailsModal(true);
      }
    } else {
      // Never confirmed, show the modal
      setShowContactDetailsModal(true);
    }
  }, [user?.preferences]);

  const onTosAccepted = () => {
    setShowTosModal(false);
  };

  const onContactDetailsConfirmed = () => {
    setShowContactDetailsModal(false);
  };

  return {
    showTosModal,
    showContactDetailsModal,
    onTosAccepted,
    onContactDetailsConfirmed,
  };
}
